import { BillingAddress } from 'src/app/shared/Models/BillingAddress';
import { HourEntry } from 'src/app/shared/Models/hourentry';
import { Project } from 'src/app/shared/Models/project-model';
import { WorkType } from 'src/app/shared/Models/worktype-model';
import { FileMetadata } from '../../Projects/Arrivals/arrivalNotice.model';

export interface OrphanRepairLinkedEntry {
  clientSummaryId: string;
  clientName: string;
  projectId: number;
  startDate: string;
  endDate: string;
  projectSummaryId: number;
}

export interface OrphanRepairUnmatchedEntry {
  clientSummaryId: string;
  clientName: string;
  projectId: number;
  startDate: string;
  endDate: string;
}

export interface OrphanRepairResult {
  executed: boolean;
  linked: OrphanRepairLinkedEntry[];
  unmatched: OrphanRepairUnmatchedEntry[];
}

export enum SummaryGroupingOption {
  None = 0,
  Worker = 1,
  WorkType = 2,
  CostLittera = 3,
  WorkClassification = 4,
  WorkLocation = 5
}

export interface GroupingOptionInfo {
  value: number;
  name: string;
  translationKey: string;
}

/** Result of the automatic Netvisor send attempted by AcceptSummary; null/undefined when not attempted. */
export interface SummaryAutoSend {
  attempted: boolean;
  succeeded: boolean;
  error: string | null;
}

export interface IBillingSummary {
  id: string;
  weekNumber?: number;
  startDate: Date;
  endDate: Date;
  projectName: string;
  projectId: number;
  confirmed: boolean;
  confirmedBy: string;
  billed: boolean;
  clientName: string;
  costCenterNumber: string;
  clientContactName: string;
  billingAddress: BillingAddress;
  ownContactName: string;
  summaryRows: SummaryRow[];
  totalSum: number;
  companies: string[];
  workTypes: string[];
  entries: HourEntry[];
  workers: string[];
  summaryTitle: string;
  summaryTag: string;
  groupingType?: number;
  groupingValue?: string;
  groupingTypeKey?: string;
}
export class BillingSummary implements IBillingSummary {
  id: string;
  createdDate:Date|undefined;
  createdBy:string|undefined;
  weekNumber?: number;
  startDate: Date;
  endDate: Date;
  projectName: string;
  projectId: number;
  confirmed: boolean;
  confirmedBy: string;
  confirmedDate:Date|undefined;
  billed: boolean;
  billedBy: string|undefined;
  billedDate:Date|undefined;
  clientName: string;
  clientId: string;
  costCenterNumber: string;
  clientContactName: string;
  billingAddress: BillingAddress;
  ownContactName: string;
  summaryRows: SummaryRow[];
  summaryRowIds: string[]=[];
  totalSum: number;
  companies: string[];
  workTypes: string[];
  entries: HourEntry[] = [];
  workers: string[];
  summaryTitle: string;
  summaryTag: string;
  groupingType?: number;
  groupingValue?: string;
  groupingTypeKey?: string;
  openInEditMode: boolean = false;
  netvisorId: string = '';
  summaryAttachment:FileMetadata|undefined;
  extraAttachments: FileMetadata[] = [];
  page: number = 1;
  /** Set by AcceptSummary when the backend tried to send the invoice automatically. */
  autoSend?: SummaryAutoSend | null;
  /** Indicates this summary is a credit note (hyvityslasku) with negative amounts */
  isCreditNote: boolean = false;
  /** Reference to the original summary this credit note was created from */
  originalSummaryId: string | undefined;
  constructor(summary: Partial<BillingSummary>) {
    this.id = '';
    this.weekNumber = 0;
    this.startDate = new Date();
    this.endDate = new Date();
    this.projectName = '';
    this.projectId = 0;
    this.confirmed = false;
    this.billed = false;
    this.clientName = '';
    this.clientId = '';
    this.costCenterNumber = '';
    this.clientContactName = '';
    this.billingAddress = new BillingAddress();
    this.ownContactName = '';
    this.summaryRows = [];
    this.totalSum = 0;
    this.companies = [];
    this.workTypes = [];
    this.workers = [];
    this.summaryTitle = '';
    this.summaryTag = '';
    this.confirmedBy = '';
    Object.assign(this, summary);
    this.summaryAttachment=summary.summaryAttachment||undefined;
    this.summaryRows =
      summary.summaryRows?.map((sr) => new SummaryRow(sr)) || [];
    this.page=summary.page??1;
  }
  static createWithDefaults(overrides: Partial<BillingSummary> = {}): BillingSummary {
    return new BillingSummary({
      id: '1',
      weekNumber: 1,
      startDate: new Date(),
      endDate: new Date(),
      projectName: 'Test Project',
      projectId: 1,
      confirmed: false,
      confirmedBy: 'Test User',
      billed: false,
      clientName: 'Test Client',
      clientId: '1',
      costCenterNumber: '12345',
      clientContactName: 'Test Contact',
      billingAddress: new BillingAddress(),
      ownContactName: 'Test Contact',
      summaryRows: [],
      totalSum: 0,
      companies: [],
      workTypes: [],
      workers: [],
      summaryTitle: 'Test Summary',
      summaryTag: 'TEST',
      ...overrides, // Allows you to override any default values
    });
  }
}

export interface ISummaryRow {
  id: string;
  isRefund: boolean;
  startDate: Date;
  endDate: Date;
  isOvertimerow: boolean;
  isExtraRow: boolean;
  useWorkTypePrice: boolean;
  workTypeName: string;
  workTypePrice: number;
  workTypeId: number;
  productCode: string;
  projectId: number;
  workerName: string;
  workerId: string;
  littera: string;
  clientId: string;
  clientCompanyName: string;
  location: string;
  message: string;
  overtimePercentage: number;
  overtimeDescription: string;
  price: number;
  overtimeMultiplier: number;
  mondayHours: number;
  tuesdayHours: number;
  wednesdayHours: number;
  thursdayHours: number;
  fridayHours: number;
  saturdayHours: number;
  sundayHours: number;
  hourSum: number;
  isNew: boolean;
  sum: number;
  isLateEntryRow?: boolean;
  isAdjustmentRow?: boolean;
  lateEntryOriginalWeekStart?: string | null;
}
export class SummaryRow implements ISummaryRow {
  isRefund: boolean;
  useWorkTypePrice: boolean;
  workTypeName: string;
  workTypePrice: number;
  workerName: string;
  littera: string;
  location: string;
  message: string;
  overtimePercentage: number;
  overtimeDescription: string;
  overtimeMultiplier: number;
  price: number;
  mondayHours: number;
  tuesdayHours: number;
  wednesdayHours: number;
  thursdayHours: number;
  fridayHours: number;
  saturdayHours: number;
  sundayHours: number;
  hourSum: number;
  sum: number;
  clientCompanyName: string;
  isNew: boolean = false;
  /** Read-only flags from the backend (week-locking step 5). */
  isLateEntryRow: boolean = false;
  isAdjustmentRow: boolean = false;
  lateEntryOriginalWeekStart: string | null = null;
  constructor(row: Partial<SummaryRow>) {
    this.isRefund = false;
    this.useWorkTypePrice = false;
    this.workTypeName = '';
    this.workTypePrice = 0.00;
    this.workerName = '';
    this.littera = '';
    this.location = '';
    this.message = '';
    this.overtimePercentage = 100;
    this.overtimeDescription = '';
    this.overtimeMultiplier = 1;
    this.price = 0;
    this.mondayHours = 0;
    this.tuesdayHours = 0;
    this.wednesdayHours = 0;
    this.thursdayHours = 0;
    this.fridayHours = 0;
    this.saturdayHours = 0;
    this.sundayHours = 0;
    this.hourSum = 0;
    this.sum = 0;
    this.clientCompanyName = '';
    Object.assign(this, row);
  }
  id: string = '';
  startDate!: Date;
  endDate!: Date;
  isOvertimerow: boolean = false;
  isExtraRow: boolean = false;
  workTypeId!: number;
  productCode: string = '';
  alvType: string = '';
  projectId!: number;
  workerId!: string;
  clientId!: string;
//   get totalAmount(): number {
//     const parseValidNumber = (value: any): number => {
//         const number = Number(value);
//         return isNaN(number) ? 0 : number;
//     };

//     return parseValidNumber(this.mondayHours) +
//            parseValidNumber(this.tuesdayHours) +
//            parseValidNumber(this.wednesdayHours) +
//            parseValidNumber(this.thursdayHours) +
//            parseValidNumber(this.fridayHours) +
//            parseValidNumber(this.saturdayHours) +
//            parseValidNumber(this.sundayHours);
// }
static createWithDefaults(overrides: Partial<SummaryRow> = {}): SummaryRow {
  return new SummaryRow({
    isRefund: false,
    useWorkTypePrice: false,
    workTypeName: 'Test WorkType',
    workTypePrice: 100,
    workerName: 'Test Worker',
    littera: 'A',
    location: 'Test Location',
    message: 'Test Message',
    overtimePercentage: 100,
    overtimeDescription: 'Test Overtime',
    overtimeMultiplier: 1,
    price: 100,
    mondayHours: 0,
    tuesdayHours: 0,
    wednesdayHours: 0,
    thursdayHours: 0,
    fridayHours: 0,
    saturdayHours: 0,
    sundayHours: 0,
    hourSum: 0,
    sum: 0,
    clientCompanyName: 'Test Client',
    ...overrides, // Allows you to override any default values
  });
}

}
// export interface HourRecord {
//     dayOfWeek: number;
//     hours: number;
// }
export interface IProjectSummary {
  startDate: Date | undefined;
  endDate: Date | undefined;
  weekNumber: number | undefined;
  CreatedTime: Date | undefined;
  projectId: number;
  billingSummaries: BillingSummary[];
}
export class ProjectSummary implements ProjectSummary {
  id: number | undefined;
  projectId: number | undefined;
  project: Project | undefined;
  projectCostCenterNumber: string | undefined;
  projectName: string | undefined;
  projectMainClientName: string | undefined;
  startDate: Date | undefined;
  endDate: Date | undefined;
  weekNumber: number | undefined;
  createdTime: Date | undefined;
  billingSummaries!: BillingSummary[];

  constructor(summary: Partial<ProjectSummary>) {
    this.id=summary.id;
    this.projectId = undefined;
    this.billingSummaries = [];
    Object.assign(this, summary);
    this.billingSummaries =
      summary.billingSummaries?.map((cs) => new BillingSummary(cs)) || [];
  }
  static createWithDefaults(overrides: Partial<ProjectSummary> = {}): ProjectSummary {
    return new ProjectSummary({
      id: 1,
      projectId: 1,
      projectCostCenterNumber: '12345',
      projectName: 'Test Project',
      projectMainClientName: 'Test Client',
      startDate: new Date(),
      endDate: new Date(),
      weekNumber: 1,
      createdTime: new Date(),
      billingSummaries: [],
      ...overrides, // Allows you to override any default values
    });
  }
  companies() {
    return this.billingSummaries.map((s) => s.clientName);
  }
  workTypeList(): string[] {
    const allNames = this.billingSummaries.flatMap((s) =>
      s.summaryRows.map((r) => r.workTypeName)
    );
    return Array.from(new Set(allNames));
  }
  employeeList(): string[] {
    const allNames = this.billingSummaries.flatMap((s) =>
      s.summaryRows.map((r) => r.workerName)
    );
    return Array.from(new Set(allNames));
  }
  confirmed(): boolean {
    return (
      this.billingSummaries.length > 0 &&
      this.billingSummaries.every((s) => s.confirmed)
    );
  }
  confirmedCount(): number {
    return this.billingSummaries.filter((s) => s.confirmed).length;
  }
  billed(): boolean {
    return (
      this.billingSummaries.length > 0 &&
      this.billingSummaries.every((s) => s.billed)
    );
  }
  billedCount(): number {
    return this.billingSummaries.filter((s) => s.billed).length;
  }
  totalSum(): number {
    return this.billingSummaries?.reduce((acc, s) => acc + s.totalSum, 0) || 0;
  }

  get rowCount(): number {
    return this.billingSummaries.reduce(
      (acc, s) => acc + s.summaryRows.length,
      0
    );
  }
}
